/* BD set fitment check — PDP verdict for SET listings (clearance/overstock).
   Grades the selected variant's bdw.set_stock positions with the shared core
   (gradeSetPositions: every axle position must fit). Shares the sitewide saved
   vehicle with the finder, vehicle bar, and catalog buy box.

   Listings without set_stock data (DATA.hasData = false) show the same vehicle
   strip but never a verdict — the vehicle is captured for fulfillment and the
   shopper is told the team hand-verifies before shipping. The year/make/model/
   trim picker is mounted open by default (Cody 2026-09-15: no free-text field;
   the four selects are the only way in). */
(() => {
  const C = window.BDFitCore;
  const root = document.querySelector("[data-bd-setcheck]");
  if (!root || !C) return;
  const DATA = JSON.parse(document.querySelector("[data-sc-data]").textContent);
  const KEY = C.resolveKey(DATA.apiKey);
  const api = KEY ? C.makeApi(KEY) : null;
  // Off-Road series skips the offset check (block emits handle + offroad)
  const GRADE_OPTS = { ignoreOffset: C.isOffroad(DATA) };
  const els = {
    vehicle: root.querySelector("[data-sc-vehicle]"),
    verdict: root.querySelector("[data-sc-verdict]"),
  };

  // currently-selected variant: the product form's id input (stock Horizon UI)
  function currentVariant() {
    const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const id = input ? +input.value : 0;
    return DATA.variants.find((v) => v.id === id)
      || DATA.variants.find((v) => v.set_stock && v.set_stock.length)
      || DATA.variants[0];
  }

  // ---- line-item properties → the order ----
  // Whatever the shopper saw here travels with the cart line so the team can
  // verify fitment on the order (Admin order page, packing slip, emails).
  // "Fit check" is always written — "Not run" is itself the signal to call.
  const PROP_KEYS = ["Vehicle", "Fit check", "Bolt pattern", "Offset", "Hub bore"];
  function lineProps(v, g) {
    const p = { "Fit check": "Not run — verify fitment before shipping" };
    if (!v) return p;
    p["Vehicle"] = v.label;
    if (!g) { p["Fit check"] = "No fitment data on this listing — verify before shipping"; return p; }
    const why = g.reasons && g.reasons.length ? g.reasons[g.reasons.length - 1] : "";
    if (g.verdict === "ok") p["Fit check"] = "Fits";
    else if (g.verdict === "warn") p["Fit check"] = `Fits with note${why ? ` (${why})` : ""}`;
    else if (g.verdict === "built") p["Fit check"] = `Built to order${why ? ` (${why})` : ""}`;
    else p["Fit check"] = `NOT COMPATIBLE — shopper overrode${why ? ` (${why})` : ""}`;
    if (g.pattern) p["Bolt pattern"] = String(g.pattern);
    if (g.offset != null) p["Offset"] = `ET${g.offset}`;
    if (g.hub != null) p["Hub bore"] = `${g.hub} mm${g.ring ? " (hub rings)" : ""}`;
    return p;
  }
  function syncLineProps(props) {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.classList.contains("payment-terms")) return; // price block's installments form
      PROP_KEYS.forEach((k) => {
        const name = `properties[${k}]`;
        let input = form.querySelector(`input[name="${name}"]`);
        if (props[k] == null) { if (input) input.remove(); return; }
        if (!input) { input = document.createElement("input"); input.type = "hidden"; input.name = name; form.appendChild(input); }
        if (input.value !== props[k]) input.value = props[k];
      });
    });
  }
  let lastProps = null;
  const observer = window.MutationObserver ? new MutationObserver(() => { if (lastProps) syncLineProps(lastProps); }) : null;
  if (observer) observer.observe(document.documentElement, { childList: true, subtree: true });

  function renderVerdict() {
    const saved = C.loadVehicle();
    const v = saved && saved.vehicle;
    const variant = currentVariant();
    const hasData = !!(variant && variant.set_stock && variant.set_stock.length);
    if (!v) {
      els.verdict.innerHTML = "";
      lastProps = lineProps(null, null);
      syncLineProps(lastProps);
      return;
    }
    if (!hasData) {
      // no grader data for this listing: capture the vehicle, promise a human check
      els.verdict.innerHTML = `<span class="manual">✓ Locked in — our fitment team hand-verifies this set against your ${v.label} before it ships.</span>`;
      lastProps = lineProps(v, null);
      syncLineProps(lastProps);
      return;
    }
    const g = C.gradeSetPositions(variant.set_stock, v, GRADE_OPTS);
    lastProps = lineProps(v, g);
    syncLineProps(lastProps);
    if (g.verdict === "ok" || g.verdict === "warn") {
      const stock = variant.qty > 0;
      els.verdict.innerHTML = `<span class="ok">✓ Fits your ${v.label}</span>`
        + (stock ? "" : ` <span class="why">— currently out of stock</span>`)
        + (g.verdict === "warn" && g.reasons && g.reasons[1] ? ` <span class="why">(${g.reasons[1]})</span>` : "");
    } else if (g.verdict === "built") {
      els.verdict.innerHTML = `<span class="built">✓ Can be built to fit your ${v.label}</span>`
        + (g.reasons && g.reasons[0] ? ` <span class="why">(${g.reasons[0]})</span>` : "");
    } else {
      els.verdict.innerHTML = `<span class="no">✗ Not compatible with your ${v.label}</span>`
        + (g.reasons && g.reasons[0] ? ` <span class="why">— ${g.reasons[0]}</span>` : "");
    }
  }

  function renderVehicleStrip() {
    const saved = C.loadVehicle();
    if (saved && saved.vehicle) {
      els.vehicle.innerHTML = `<div class="bdbb-veh bdbb-veh--set">
        <div class="bdbb-veh__row">
          <span>Your vehicle: <b>${saved.vehicle.label}</b></span></div>
        <div class="bdbb-veh__links">
          <button type="button" class="bdbb-link" data-sc-change>Change</button>
          <button type="button" class="bdbb-link" data-sc-clear>Clear</button>
        </div>
      </div><div class="bdbb-picker" data-sc-picker hidden></div>`;
      els.vehicle.querySelector("[data-sc-clear]").onclick = () => { C.clearVehicle(); C.writeVehicleToUrl(null); renderAll(); };
      els.vehicle.querySelector("[data-sc-change]").onclick = () => togglePicker();
    } else {
      // no vehicle yet: sell the check and open the four selects straight away
      els.vehicle.innerHTML = `<div class="bd-setcheck__pitch">
        <div class="bd-setcheck__pitch-title">Perfect fit, handled — tell us what you drive</div>
        <div class="bd-setcheck__pitch-copy">Pick your year, make, model and trim. Our fitment team hand-checks every set against your exact vehicle before it ships, so you never have to guess about bolt pattern, offset or clearance.</div>
      </div><div class="bdbb-picker" data-sc-picker></div>`;
      togglePicker();
    }
  }

  function togglePicker() {
    const box = els.vehicle.querySelector("[data-sc-picker]");
    if (!api) { box.hidden = false; box.innerHTML = `<div class="bdbb-err">Fitment lookup not configured.</div>`; return; }
    box.hidden = false;
    // keep the page URL shareable (?make=&model=&year=&trim=) like every other picker
    C.mountPicker(box, { api, onVehicle: () => { C.writeVehicleToUrl(C.loadVehicle()); renderAll(); } });
  }

  function renderAll() { renderVehicleStrip(); renderVerdict(); }

  // re-grade when the shopper switches variants. Dropdown options (Size) fire a
  // native change; swatch/pill options (Lug Pattern) don't — the picker dispatches
  // shopify:product:select and morphs the section, and its promise settles only
  // after the form's [name="id"] input reflects the new variant.
  document.addEventListener("change", (e) => {
    if (e.target.closest && e.target.closest('form[action*="/cart/add"], variant-picker, .variant-picker')) {
      setTimeout(renderVerdict, 50);
    }
  });
  document.addEventListener("shopify:product:select", (e) => {
    const after = e.promise && typeof e.promise.then === "function" ? e.promise : Promise.resolve();
    after.catch(() => {}).then(() => setTimeout(renderVerdict, 0));
  });

  renderAll();
  if (api) C.resolveVehicleFromUrl(api).then((r) => { if (r) renderAll(); }).catch(() => {});
})();
